/**
 * Phase 24.7.3 T3 — manual workout entry form.
 *
 * Used inside a Modal launched from Dashboard. Submits to
 * POST /api/workouts/manual which auto-computes TSS = duration_min ×
 * rpe_factor² unless user provides explicit override.
 *
 * Sport vocabulary intentionally broader than auto-sync set (run/bike/swim/
 * strength) — manual entries cover yoga, walks, ski tours, rowing erg.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import {
  createManualWorkout,
  type ManualWorkoutCreate,
} from "@/api/workouts";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/stores/toast";

const SPORTS: ManualWorkoutCreate["sport"][] = [
  "run",
  "bike",
  "swim",
  "strength",
  "yoga",
  "walk",
  "hike",
  "row",
  "ski",
  "other",
];

function todayISO(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function computeAutoTss(durationMin: number, rpe: number): number {
  if (durationMin < 1 || rpe < 1 || rpe > 10) return 0;
  const factor = 0.3 + (rpe - 1) * (1 / 9);
  return Math.floor(durationMin * factor * factor);
}

interface Props {
  onClose: () => void;
}

export function ManualWorkoutForm({ onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();

  const [date, setDate] = useState<string>(todayISO());
  const [sport, setSport] = useState<ManualWorkoutCreate["sport"]>("run");
  const [durationMin, setDurationMin] = useState<number>(60);
  const [rpe, setRpe] = useState<number>(5);
  const [overrideTss, setOverrideTss] = useState<boolean>(false);
  const [tssOverrideValue, setTssOverrideValue] = useState<number>(0);
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const autoTss = useMemo(() => computeAutoTss(durationMin, rpe), [durationMin, rpe]);
  const today = useMemo(() => todayISO(), []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!date || durationMin < 1 || durationMin > 600 || rpe < 1 || rpe > 10) {
      setError(t("workout.manual.error_invalid"));
      return;
    }
    setSubmitting(true);
    try {
      const payload: ManualWorkoutCreate = {
        date,
        sport,
        duration_min: durationMin,
        rpe,
        tss_override: overrideTss ? tssOverrideValue : null,
        notes: notes.trim() || null,
      };
      await createManualWorkout(payload);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["workouts"] }),
        qc.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
      toast.show({ message: t("workout.manual.saved_toast") });
      onClose();
    } catch (err) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? ((err as { response?: { data?: { detail?: string } } }).response
              ?.data?.detail ?? null)
          : null;
      setError(msg || t("workout.manual.error_save"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-[12px] font-medium text-navy mb-1">
          {t("workout.manual.field_date")}
        </label>
        <input
          type="date"
          value={date}
          max={today}
          onChange={(e) => setDate(e.target.value)}
          required
          className="w-full rounded-lg border border-cream-border bg-cream px-3 py-2 text-[14px] text-navy-deep focus:border-amber focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-[12px] font-medium text-navy mb-1">
          {t("workout.manual.field_sport")}
        </label>
        <select
          value={sport}
          onChange={(e) => setSport(e.target.value as ManualWorkoutCreate["sport"])}
          className="w-full rounded-lg border border-cream-border bg-cream px-3 py-2 text-[14px] text-navy-deep focus:border-amber focus:outline-none"
        >
          {SPORTS.map((s) => (
            <option key={s} value={s}>
              {t(`sport.${s}`, { defaultValue: s })}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-navy mb-1">
          {t("workout.manual.field_duration")}
          <span className="ml-2 text-taupe">({t("workout.manual.field_duration_unit")})</span>
        </label>
        <input
          type="number"
          min={1}
          max={600}
          value={durationMin}
          onChange={(e) => setDurationMin(Number(e.target.value))}
          required
          className="w-full rounded-lg border border-cream-border bg-cream px-3 py-2 text-[14px] text-navy-deep focus:border-amber focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-[12px] font-medium text-navy mb-1">
          {t("workout.manual.field_rpe")}: <span className="font-mono text-navy-deep">{rpe}</span>
        </label>
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={rpe}
          onChange={(e) => setRpe(Number(e.target.value))}
          className="w-full accent-amber"
        />
        <div className="text-[11px] text-taupe mt-1">{t("workout.manual.field_rpe_hint")}</div>
      </div>

      <div className="rounded-lg bg-cream-border/40 px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-taupe">{t("workout.manual.field_tss_auto")}</span>
          <span className="font-mono text-[14px] font-medium text-navy-deep">
            {overrideTss ? tssOverrideValue : autoTss}
          </span>
        </div>
        <label className="mt-2 flex items-center gap-2 text-[12px] text-navy">
          <input
            type="checkbox"
            checked={overrideTss}
            onChange={(e) => {
              setOverrideTss(e.target.checked);
              if (e.target.checked) setTssOverrideValue(autoTss);
            }}
            className="h-4 w-4 accent-amber"
          />
          {t("workout.manual.field_tss_override_toggle")}
        </label>
        {overrideTss ? (
          <input
            type="number"
            min={0}
            max={500}
            value={tssOverrideValue}
            onChange={(e) => setTssOverrideValue(Number(e.target.value))}
            className="mt-2 w-full rounded-lg border border-cream-border bg-cream px-3 py-2 text-[14px] text-navy-deep focus:border-amber focus:outline-none"
          />
        ) : null}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-navy mb-1">
          {t("workout.manual.field_notes")}
          <span className="ml-2 text-taupe">{notes.length}/500</span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 500))}
          rows={3}
          className="w-full rounded-lg border border-cream-border bg-cream px-3 py-2 text-[14px] text-navy-deep focus:border-amber focus:outline-none"
        />
      </div>

      {error ? (
        <div className="rounded-md bg-red-50 px-3 py-2 text-[12px] text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={submitting}
          className="flex-1"
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={submitting}
          loading={submitting}
          className="flex-1"
        >
          {t("workout.manual.save_button")}
        </Button>
      </div>
    </form>
  );
}
